import axios, { AxiosResponse } from 'axios';
import { doc, getDoc, query, collection, where, getDocs, deleteDoc, addDoc, setDoc, updateDoc, arrayUnion, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';

export interface WooCommerceCredentials {
  url: string;
  consumerKey: string;
  consumerSecret: string;
  storeId?: string;
  storeName?: string;
}

export interface WooCommerceOrder {
  id: number;
  status: string;
  total: string;
  date_created: string;
  billing: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  };
  line_items: Array<{
    id: number;
    name: string;
    quantity: number;
    price: number;
    [key: string]: any;
  }>;
  [key: string]: any;
}

export interface ProductWithWooCommerce {
  name: string;
  description: string;
  sku: string;
  regularPrice: number;
  salePrice: number;
  stockQuantity: number;
  stockStatus: string;
  weight: number;
  length: number;
  width: number;
  height: number;
  woocommerce?: {
    woocommerce_id: number;
  };
}

// Mapeamento bidirecional entre status do EMTUACASA e WooCommerce
export const STATUS_MAPPING = {
  emtuacasaToWoo: {
    'pending': 'pending',           // Pendente -> Pendente
    'preparation': 'processing',    // Em preparação -> Processando
    'awaitingStock': 'on-hold',    // Aguardando Stock -> Em espera
    'awaitingPickup': 'processing', // Aguardando Recolha -> Processando
    'shipped': 'completed',         // Enviado -> Concluído
    'delivered': 'completed',       // Entregue -> Concluído
    'cancelled': 'cancelled',       // Cancelado -> Cancelado
    'returned': 'refunded',         // Devolvido -> Reembolsado
    'awaiting_payment': 'pending'   // Aguardando Pagamento -> Pendente
  },
  wooToEmtuacasa: {
    'pending': 'pending',           // Pendente -> Pendente
    'processing': 'preparation',    // Processando -> Em preparação
    'on-hold': 'preparation',       // Em espera -> Em preparação
    'completed': 'shipped',         // Concluído -> Enviado
    'cancelled': 'cancelled',       // Cancelado -> Cancelado
    'refunded': 'returned',         // Reembolsado -> Devolvido
    'failed': 'cancelled',          // Falhou -> Cancelado
    'trash': 'cancelled',           // Lixeira -> Cancelado
    'pronto-para-envio': 'shipped', // Pronto para envio -> Enviado
    'dados-errados': 'cancelled',   // Dados errados -> Cancelado
    'checkout-draft': 'pending'     // Rascunho -> Pendente
  }
} as const;

// Função para atualizar o status no Firebase
export async function updateOrderStatus(orderId: string, newStatus: string): Promise<void> {
  try {
    const orderRef = doc(db, 'orders', orderId);
    const orderDoc = await getDoc(orderRef);
    
    if (!orderDoc.exists()) {
      throw new Error('Order not found');
    }

    const orderData = orderDoc.data();
    const now = new Date().toISOString();
    
    // Update status and add to history
    await updateDoc(orderRef, {
      status: newStatus,
      lastStatusUpdate: now,
      statusHistory: arrayUnion({
        status: newStatus,
        date: now,
        source: 'internal',
        previousStatus: orderData.status
      }),
      updatedAt: now
    });
  } catch (error) {
    console.error('Error updating order status:', error);
    throw error;
  }
}

// Função para atualizar o status no WooCommerce
export async function updateWooCommerceOrderStatus(
  integrationId: string,
  orderId: string,
  newStatus: string
): Promise<void> {
  try {
    // Get integration settings
    const integrationRef = doc(db, 'integrations', integrationId);
    const integrationSnap = await getDoc(integrationRef);
    
    if (!integrationSnap.exists()) {
      throw new Error('Integration not found');
    }

    const integrationData = integrationSnap.data();
    const config = integrationData.config;

    if (!config || !config.url || !config.consumerKey || !config.consumerSecret) {
      throw new Error('Missing WooCommerce configuration');
    }

    if (!Array.isArray(config.orderStatusMappings)) {
      console.error('Invalid orderStatusMappings:', config.orderStatusMappings);
      throw new Error('Invalid status mappings configuration');
    }

    // Get the order from Firebase
    const orderRef = doc(db, 'orders', orderId);
    const orderSnap = await getDoc(orderRef);
    
    if (!orderSnap.exists()) {
      throw new Error('Order not found');
    }

    const orderData = orderSnap.data();
    if (!orderData.sourceOrderId) {
      throw new Error('WooCommerce order ID not found in order data');
    }

    // Find the matching status mapping
    const statusMapping = config.orderStatusMappings.find(
      (mapping: any) => mapping && typeof mapping === 'object' && mapping.emtuacasa === newStatus
    );

    if (!statusMapping || !statusMapping.store) {
      console.error('Available mappings:', config.orderStatusMappings);
      console.error('Requested status:', newStatus);
      throw new Error(`No mapping found for status: ${newStatus}`);
    }

    // Update the order in WooCommerce
    const wooCommerceUrl = `${config.url}/wp-json/wc/v3/orders/${orderData.sourceOrderId}`;
    const authParams = `consumer_key=${encodeURIComponent(config.consumerKey)}&consumer_secret=${encodeURIComponent(config.consumerSecret)}`;
    
    const response = await fetch(`${wooCommerceUrl}?${authParams}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        status: statusMapping.store
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`WooCommerce API error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    console.log(`Successfully updated WooCommerce order ${orderData.sourceOrderId} to status: ${statusMapping.store}`);
  } catch (error: any) {
    console.error('Error updating WooCommerce order status:', error);
    throw new Error(error.message || 'Failed to update WooCommerce order status');
  }
}

// Função para observar mudanças nos pedidos do Firebase
export function watchOrderStatusChanges(integrationId: string): () => void {
  const ordersRef = collection(db, 'orders');
  
  // Criar query para pedidos do WooCommerce apenas
  const q = query(
    ordersRef,
    where('source', '==', 'woocommerce')
  );

  // Retornar a função de unsubscribe diretamente
  return onSnapshot(q, async (snapshot) => {
    try {
      const changes = snapshot.docChanges();
      
      for (const change of changes) {
        if (change.type === 'modified') {
          const orderData = change.doc.data();
          
          // Se o status mudou e veio de uma fonte interna
          if (orderData.statusHistory?.length > 0) {
            const lastStatus = orderData.statusHistory[orderData.statusHistory.length - 1];
            if (lastStatus.source === 'internal') {
              // Atualizar status no WooCommerce
              await updateWooCommerceOrderStatus(
                integrationId,
                orderData.orderId,
                orderData.status
              );
            }
          }
        }
      }
    } catch (error) {
      console.error('Error in watchOrderStatusChanges:', error);
    }
  });
}

// Função para processar pedidos do WooCommerce
export async function processWooCommerceOrder(orderData: any, existingStatus?: string, companyId?: string): Promise<any> {
  // Validação rigorosa dos dados do pedido
  if (!orderData || typeof orderData.id === 'undefined') {
    console.error('Dados do pedido inválidos ou sem ID:', orderData);
    return null;
  }

  // Extrair dados do cliente
  const customer = {
    name: `${orderData.billing?.first_name || ''} ${orderData.billing?.last_name || ''}`.trim(),
    email: orderData.billing?.email || '',
    phone: orderData.billing?.phone || '',
    address: {
      street: orderData.billing?.address_1 || '',
      number: '',  // WooCommerce não tem campo específico para número
      complement: orderData.billing?.address_2 || '',
      neighborhood: '',  // WooCommerce não tem campo específico para bairro
      city: orderData.billing?.city || '',
      state: orderData.billing?.state || '',
      country: orderData.billing?.country || '',
      postalCode: orderData.billing?.postcode || ''
    }
  };

  // Extrair dados de envio
  const shippingAddress = {
    name: `${orderData.shipping?.first_name || ''} ${orderData.shipping?.last_name || ''}`.trim(),
    company: orderData.shipping?.company || '',
    address_1: orderData.shipping?.address_1 || '',
    address_2: orderData.shipping?.address_2 || '',
    city: orderData.shipping?.city || '',
    state: orderData.shipping?.state || '',
    postcode: orderData.shipping?.postcode || '',
    country: orderData.shipping?.country || '',
    phone: orderData.shipping?.phone || orderData.billing?.phone || ''
  };

  // Processar itens do pedido
  const items = orderData.line_items?.map((item: any) => ({
    id: item.id?.toString(),
    name: item.name || '',
    quantity: item.quantity || 0,
    price: parseFloat(item.price || '0'),
    total: parseFloat(item.total || '0'),
    sku: item.sku || '',
    productId: item.product_id?.toString() || '',
    variationId: item.variation_id?.toString() || '',
    metadata: item.meta_data || [],
    image: item.image || null
  })) || [];

  // Determinar o status do pedido
  let orderStatus = existingStatus;
  
  // Se não houver status existente ou se o status do WooCommerce mudou
  if (!existingStatus || (orderData.status && STATUS_MAPPING.wooToEmtuacasa[orderData.status] !== existingStatus)) {
    // Mapear o status do WooCommerce para o status do EMTUACASA
    orderStatus = STATUS_MAPPING.wooToEmtuacasa[orderData.status] || 'pending';
    
    // Regras especiais de status
    if (orderData.status === 'processing') {
      orderStatus = 'preparation'; // Processando -> Em preparação
    } else if (orderData.status === 'completed') {
      // Se já estiver como "delivered" ou "returned", manter esse status
      if (existingStatus === 'delivered' || existingStatus === 'returned') {
        orderStatus = existingStatus;
      } else {
        orderStatus = 'shipped'; // Concluído -> Enviado
      }
    }
  }

  // Retornar o pedido processado como um objeto simples (não uma Promise)
  return {
    orderId: orderData.id.toString(),
    displayOrderId: `WC${orderData.id}`,
    customer,
    shippingAddress,
    items,
    source: 'woocommerce',
    sourceData: orderData,
    sourceOrderId: orderData.id.toString(),
    status: orderStatus,
    // CAMPOS CRÍTICOS DA EMPRESA - Sempre no topo
    companyId: companyId,  // <- AQUI: Adicionado companyId
    sellerId: companyId,   // <- AQUI: Adicionado sellerId
    // Valores
    total: parseFloat(orderData.total || '0'),
    subtotal: parseFloat(orderData.subtotal || '0'),
    totalTax: parseFloat(orderData.total_tax || '0'),
    shippingTotal: parseFloat(orderData.shipping_total || '0'),
    discountTotal: parseFloat(orderData.discount_total || '0'),
    // Datas
    createdAt: orderData.date_created || new Date().toISOString(),
    updatedAt: orderData.date_modified || new Date().toISOString(),
    lastModified: orderData.date_modified || new Date().toISOString(),
    lastStatusUpdate: new Date().toISOString(),
    lastUpdated: new Date().toISOString(),
    // Informações de pagamento
    paymentMethod: orderData.payment_method_title || orderData.payment_method || '',
    customerNote: orderData.customer_note || '',
    // Histórico e metadados
    statusHistory: [{
      status: orderStatus,
      date: new Date().toISOString(),
      source: 'woocommerce',
      previousStatus: existingStatus || null
    }],
    shipping_lines: orderData.shipping_lines || [],
    meta_data: orderData.meta_data || []
  };
}

export async function getWooCommerceOrderStatuses(credentials: WooCommerceCredentials): Promise<{ id: string; name: string; }[]> {
  try {
    if (!credentials?.url) {
      throw new Error('URL do WooCommerce não fornecida');
    }

    // Ensure URL is properly formatted and remove any trailing slashes
    const baseUrl = credentials.url.replace(/\/+$/, '');

    // Use the orders/totals endpoint that shows all statuses
    const endpoint = `/wp-json/wc/v3/reports/orders/totals?consumer_key=${encodeURIComponent(credentials.consumerKey)}&consumer_secret=${encodeURIComponent(credentials.consumerSecret)}`;

    // Log the request URL for debugging
    console.log('Requesting WooCommerce order totals from:', `${baseUrl}${endpoint}`);

    // Make a direct request to WooCommerce
    const response = await axios.get(`${baseUrl}${endpoint}`, {
      timeout: 30000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    
    if (response.data && Array.isArray(response.data)) {
      // Map the response to our format, keeping the original names from WooCommerce
      const statuses = response.data.map(item => ({
        id: item.slug,
        name: item.name
      }));

      console.log('Found WooCommerce statuses:', statuses);
      return statuses;
    }

    // Fallback to default statuses if the API response is not in the expected format
    console.log('Invalid API response format, using default statuses');
    return getDefaultOrderStatuses();

  } catch (error: any) {
    console.error('Error getting WooCommerce order statuses:', error);
    if (error.response) {
      console.error('Error response:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
    }

    // Return standard WooCommerce order statuses as fallback
    console.log('Returning default WooCommerce statuses due to API error');
    return getDefaultOrderStatuses();
  }
}

// Helper function to return default WooCommerce order statuses as fallback
function getDefaultOrderStatuses(): { id: string; name: string; }[] {
  return [
    { id: 'pending', name: 'Pagamento pendente' },
    { id: 'processing', name: 'Em processamento' },
    { id: 'on-hold', name: 'Aguarda confirmação de pagamento' },
    { id: 'completed', name: 'Concluída' },
    { id: 'cancelled', name: 'Cancelada' },
    { id: 'refunded', name: 'Reembolsada' },
    { id: 'failed', name: 'Falhada' },
    { id: 'pronto-para-envio', name: 'Pronto para enviar' },
    { id: 'dados-errados', name: 'Dados errados' },
    { id: 'checkout-draft', name: 'Rascunho' }
  ];
}

export async function getWooCommerceOrders(credentials: WooCommerceCredentials): Promise<AxiosResponse<WooCommerceOrder[]>> {
  if (!credentials?.url) {
    throw new Error('URL do WooCommerce não fornecida');
  }

  if (!credentials?.consumerKey || !credentials?.consumerSecret) {
    throw new Error('Credenciais do WooCommerce não fornecidas');
  }

  try {
    // Remove trailing slash from URL if present
    const baseUrl = credentials.url.replace(/\/$/, '');
    
    // Construir a URL completa com as credenciais
    const fullUrl = `${baseUrl}/wp-json/wc/v3/orders`;
    
    // Parâmetros da query
    const params = {
      consumer_key: credentials.consumerKey,
      consumer_secret: credentials.consumerSecret,
      per_page: '100',
      orderby: 'date',
      order: 'desc'
    };

    const response = await axios.get<WooCommerceOrder[]>(fullUrl, { params });

    // Se a resposta estiver vazia, retornar um array vazio
    if (!response.data) {
      return { ...response, data: [] };
    }

    // Garantir que a resposta seja um array
    const orders = Array.isArray(response.data) ? response.data : [];
    return { ...response, data: orders };

  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorResponse = error.response;
      
      if (errorResponse?.status === 401) {
        throw new Error('Credenciais do WooCommerce inválidas');
      }
      
      if (errorResponse?.status === 404) {
        throw new Error('API do WooCommerce não encontrada. Verifique a URL da loja');
      }
      
      if (errorResponse?.data?.message) {
        throw new Error(`Erro na API do WooCommerce: ${errorResponse.data.message}`);
      }

      throw new Error(`Erro na API do WooCommerce: ${errorResponse?.statusText || error.message}`);
    }
    
    throw new Error(`Erro ao buscar pedidos: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
  }
}

export async function getWooCommerceProducts(credentials: WooCommerceCredentials, page: number = 1, perPage: number = 100): Promise<{ products: any[], totalPages: number }> {
  try {
    const response = await axios.get(`${credentials.url}/wp-json/wc/v3/products`, {
      params: {
        per_page: perPage,
        page: page,
        status: 'publish',
      },
      auth: {
        username: credentials.consumerKey,
        password: credentials.consumerSecret
      }
    });

    const totalPages = parseInt(response.headers['x-wp-totalpages'] || '1');
    return { 
      products: response.data,
      totalPages
    };
  } catch (error) {
    console.error('Error fetching WooCommerce products:', error);
    throw error;
  }
}

export async function getWooCommerceConfig(companyId: string): Promise<WooCommerceCredentials | null> {
  if (!companyId) {
    throw new Error('ID da empresa não fornecido');
  }

  try {
    // Primeiro, tentar buscar da coleção de integrações
    const integrationsQuery = query(
      collection(db, 'integrations'),
      where('companyId', '==', companyId),
      where('type', '==', 'woocommerce'),
      where('active', '==', true)
    );
    
    const integrationsSnapshot = await getDocs(integrationsQuery);
    
    if (!integrationsSnapshot.empty) {
      const integration = integrationsSnapshot.docs[0].data();
      return {
        url: integration.config.url,
        consumerKey: integration.config.consumerKey,
        consumerSecret: integration.config.consumerSecret,
        storeId: integration.config.storeId,
        storeName: integration.config.storeName
      };
    }

    // Se não encontrar na coleção de integrações, tentar buscar do documento da empresa
    const companyDoc = await getDoc(doc(db, 'companies', companyId));
    
    if (companyDoc.exists()) {
      const companyData = companyDoc.data();
      if (companyData.woocommerce) {
        return {
          url: companyData.woocommerce.url,
          consumerKey: companyData.woocommerce.consumerKey,
          consumerSecret: companyData.woocommerce.consumerSecret,
          storeId: companyData.woocommerce.storeId,
          storeName: companyData.woocommerce.storeName
        };
      }
    }

    console.log(`Nenhuma configuração WooCommerce encontrada para a empresa ${companyId}`);
    return null;
  } catch (error) {
    console.error('Erro ao buscar configuração do WooCommerce:', error);
    throw new Error('Erro ao buscar configuração do WooCommerce: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
  }
}

export async function syncProductToWooCommerce(credentials: WooCommerceCredentials, product: ProductWithWooCommerce): Promise<AxiosResponse<any>> {
  try {
    const baseUrl = credentials.url.replace(/\/$/, '');
    
    const method = product.woocommerce?.woocommerce_id ? 'PUT' : 'POST';
    const endpoint = product.woocommerce?.woocommerce_id 
      ? `/wp-json/wc/v3/products/${product.woocommerce.woocommerce_id}`
      : '/wp-json/wc/v3/products';

    const response = await axios({
      url: `${baseUrl}${endpoint}`,
      method,
      auth: {
        username: credentials.consumerKey,
        password: credentials.consumerSecret
      },
      data: {
        name: product.name,
        description: product.description,
        sku: product.sku,
        regular_price: product.regularPrice.toString(),
        sale_price: product.salePrice?.toString(),
        stock_quantity: product.stockQuantity,
        stock_status: product.stockStatus,
        dimensions: {
          length: product.length?.toString(),
          width: product.width?.toString(),
          height: product.height?.toString()
        },
        weight: product.weight?.toString()
      }
    });

    return response;
  } catch (error) {
    console.error('Error syncing product to WooCommerce:', error);
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const message = error.response?.data?.message || error.message;
      throw new Error(`Error ${status}: ${message}`);
    }
    throw new Error('Failed to sync product with WooCommerce. Please try again.');
  }
}

export async function unlinkWooCommerceProduct(credentials: WooCommerceCredentials, woocommerceId: number): Promise<AxiosResponse<any>> {
  try {
    const baseUrl = credentials.url.replace(/\/$/, '');
    
    const response = await axios({
      url: `${baseUrl}/wp-json/wc/v3/products/${woocommerceId}`,
      method: 'DELETE',
      auth: {
        username: credentials.consumerKey,
        password: credentials.consumerSecret
      },
      params: {
        force: true
      }
    });

    return response;
  } catch (error) {
    console.error('Error unlinking WooCommerce product:', error);
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const message = error.response?.data?.message || error.message;
      throw new Error(`Error ${status}: ${message}`);
    }
    throw new Error('Failed to unlink product from WooCommerce. Please try again.');
  }
}

export async function syncProductStock(credentials: WooCommerceCredentials, woocommerceId: number, quantity: number): Promise<AxiosResponse<any>> {
  try {
    const baseUrl = credentials.url.replace(/\/$/, '');
    
    const response = await axios({
      url: `${baseUrl}/wp-json/wc/v3/products/${woocommerceId}`,
      method: 'PUT',
      auth: {
        username: credentials.consumerKey,
        password: credentials.consumerSecret
      },
      data: {
        stock_quantity: quantity
      }
    });

    return response;
  } catch (error) {
    console.error('Error syncing product stock:', error);
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const message = error.response?.data?.message || error.message;
      throw new Error(`Error ${status}: ${message}`);
    }
    throw new Error('Failed to sync product stock with WooCommerce. Please try again.');
  }
}

export async function deleteWooCommerceOrder(orderId: string): Promise<boolean> {
  try {
    const orderRef = doc(db, 'orders', orderId);
    await deleteDoc(orderRef);
    return true;
  } catch (error) {
    console.error('Error deleting WooCommerce order:', error);
    return false;
  }
}

export async function deleteWooCommerceOrderFromFirebase(orderId: string): Promise<void> {
  try {
    // Delete the order document from Firebase
    await deleteDoc(doc(db, 'orders', orderId));
  } catch (error) {
    console.error('Error deleting WooCommerce order from Firebase:', error);
    throw error;
  }
};

export async function removeDuplicateOrders(companyId: string): Promise<void> {
  try {
    if (!companyId) {
      throw new Error('Company ID is required');
    }

    // Get all orders for the company
    const ordersQuery = query(
      collection(db, 'orders'),
      where('source', '==', 'woocommerce'),
      where('companyId', '==', companyId)
    );
    
    const ordersSnapshot = await getDocs(ordersQuery);
    const orders = ordersSnapshot.docs.map(doc => ({
      id: doc.id,
      orderId: doc.data().orderId,
      updatedAt: doc.data().updatedAt,
    }));

    // Group orders by WooCommerce orderId
    const orderGroups: { [key: string]: typeof orders } = {};
    for (const order of orders) {
      if (!order.orderId) continue;
      if (!orderGroups[order.orderId]) {
        orderGroups[order.orderId] = [];
      }
      orderGroups[order.orderId].push(order);
    }

    // Remove duplicates keeping only the most recently updated one
    for (const [sourceOrderId, orderDocs] of orderGroups) {
      if (orderDocs.length > 1) {
        // Sort by updatedAt in descending order
        orderDocs.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
        
        // Keep the first one (most recent) and delete the rest
        for (let i = 1; i < orderDocs.length; i++) {
          await deleteDoc(doc(db, 'orders', orderDocs[i].id));
          console.log(`Deleted duplicate order: ${orderDocs[i].id} for WooCommerce order ${sourceOrderId}`);
        }
      }
    }

    console.log('Duplicate order cleanup completed');
  } catch (error) {
    console.error('Error removing duplicate orders:', error);
    throw error;
  }
}

export async function syncWooCommerceOrders(
  integrationId: string,
  onProgress?: (progress: { total: number; current: number; message: string }) => void,
  options?: { fromDate?: string }
): Promise<void> {
  try {
    const integration = await getDoc(doc(db, 'integrations', integrationId));
    if (!integration.exists()) {
      throw new Error('Integration not found');
    }

    const integrationData = integration.data();
    const { config, companyId } = integrationData;
    const { consumerKey, consumerSecret, url, orderStatusMappings } = config;

    if (!companyId) {
      throw new Error('Company ID not found in integration');
    }

    if (!orderStatusMappings || !Array.isArray(orderStatusMappings)) {
      throw new Error('Status mapping not configured in integration settings');
    }

    // Create a mapping object from the orderStatusMappings array
    const statusMapping: { [key: string]: string } = {};
    orderStatusMappings.forEach(mapping => {
      if (mapping.store) {
        // Verificar tanto o campo emtuacasa quanto o campo lognow
        const targetStatus = mapping.lognow || mapping.emtuacasa;
        if (targetStatus) {
          statusMapping[mapping.store] = targetStatus;
        }
      }
    });

    console.log('Using status mapping from integration:', statusMapping);

    // Build URL with date filter if provided and get all orders
    let apiUrl = `${url}/wp-json/wc/v3/orders?consumer_key=${consumerKey}&consumer_secret=${consumerSecret}&per_page=100&status=any`;
    if (options?.fromDate) {
      apiUrl += `&after=${options.fromDate}T00:00:00`;
    }

    const response = await fetch(apiUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch WooCommerce orders: ${response.statusText}`);
    }
    const orders = await response.json();

    onProgress?.({
      total: orders.length,
      current: 0,
      message: 'A importar encomendas...'
    });

    let current = 0;
    for (const order of orders) {
      // Check for existing order
      const existingOrderQuery = query(
        collection(db, 'orders'),
        where('source', '==', 'woocommerce'),
        where('sourceOrderId', '==', order.id.toString()),
        where('companyId', '==', companyId)
      );
      const existingOrderSnapshot = await getDocs(existingOrderQuery);
      
      let orderRef;
      if (!existingOrderSnapshot.empty) {
        // Update existing order
        orderRef = existingOrderSnapshot.docs[0].ref;
      } else {
        // Create new order
        orderRef = doc(collection(db, 'orders'));
      }

      // Get mapped status from integration settings
      const wooStatus = order.status;
      const mappedStatus = statusMapping[wooStatus];

      if (!mappedStatus) {
        console.warn(`Status mapping not found for WooCommerce status: "${wooStatus}". Order ID: ${order.id}. Using original status.`);
        // Em vez de ignorar o pedido, vamos processá-lo com o estado original
        const processedOrder = await processWooCommerceOrder(order, wooStatus, companyId);
        
        if (processedOrder) {
          const orderData = {
            ...processedOrder,
            source: 'woocommerce',
            sourceOrderId: order.id.toString(),
            sourceData: order,
            companyId, // Garantir que o companyId está definido
            createdAt: order.date_created || order.date_created_gmt,
            updatedAt: new Date().toISOString(),
            status: wooStatus // Usar o estado original
          };
          
          await setDoc(orderRef, orderData, { merge: true });
          
          current++;
          onProgress?.({ total: orders.length, current, message: `Importada encomenda ${order.id}` });
        }
        continue;
      }

      console.log(`Order ${order.id}: WooCommerce status "${wooStatus}" mapped to "${mappedStatus}"`);

      // Process order
      const processedOrder = await processWooCommerceOrder(order, undefined, companyId);
      
      if (processedOrder) {
        const orderData = {
          ...processedOrder,
          source: 'woocommerce',
          sourceOrderId: order.id.toString(),
          sourceData: order,
          companyId, // Garantir que o companyId está definido
          createdAt: order.date_created || order.date_created_gmt,
          updatedAt: new Date().toISOString(),
          status: mappedStatus,
          lastStatusUpdate: new Date().toISOString(),
          statusHistory: arrayUnion({
            status: mappedStatus,
            date: new Date().toISOString(),
            source: 'woocommerce',
            originalStatus: wooStatus
          })
        };

        await setDoc(orderRef, orderData, { merge: true });

        current++;
        onProgress?.({
          total: orders.length,
          current,
          message: `Importada encomenda ${current} de ${orders.length}`
        });
      }
    }

    return;
  } catch (error) {
    console.error('Error syncing WooCommerce orders:', error);
    throw error;
  }
}

async function getWooCommerceCustomer(credentials: WooCommerceCredentials, customerId: number): Promise<any> {
  const { url, consumerKey, consumerSecret } = credentials;
  const response = await axios.get(
    `${url}/wp-json/wc/v3/customers/${customerId}`,
    {
      params: {
        consumer_key: consumerKey,
        consumer_secret: consumerSecret
      }
    }
  );
  return response.data;
}
